
import { Modal } from 'bootstrap';

import { defineComponent, ref, inject, computed, onUnmounted } from 'vue';

import { Select } from '@/components/input-elements';

import { Apollo, Notify } from '@/core/services';

import { GET_PRODUCT_GROUP_PRODUCTS } from '@/modules/catalog/product-groups/graphql/Queries';

import { UPDATE_GROUP_PRODUCTS } from '@/modules/catalog/product-groups/graphql/Mutations';

import { EditProductGroupForm } from '@/modules/catalog/product-groups/interfaces';

import { useI18n } from 'vue-i18n';

import InnerLoader from '@/components/InnerLoader.vue';

import { CUP_AGREEMENT_PRODUCT } from '@/modules/catalog/products/graphql/Queries';

const ASSIGNMENTS_LIMIT = 10;

const CATALOG_PRODUCTS_LIMIT = 10000;

export default defineComponent({
    name: 'edit customer group',

    components: {
        Select,

        InnerLoader
    },

    setup() {
        const loader = ref(false);

        const products = ref([]) as Record<any, any>;

        const modal: any = ref();

        const loading = ref(false);

        const emitter: any = inject('emitter');

        const current_page = ref(1);

        const i18n = useI18n();

        const editProductGroupForm = ref<null | HTMLFormElement>(null);

        const group_products = ref([]) as Record<any, any>;

        const product_group_id = ref();

        const assignedProductsData = ref([]) as Record<any, any>;

        const assignedProducts = ref([]) as Record<any, any>;

        const assignedProduct = ref([]) as Record<any, any>;

        const pagination = ref({}) as Record<any, any>;

        const clickHandler = ref() as Record<any, any>;

        /** Full set of assignment product ids (all pages) for submit */

        const accumulatedAssignmentIds = ref(new Set<number>());

        const editProductGroupData = ref<EditProductGroupForm>({
            id: null,

            name: '',

            uuid: '',

            allow_discount: false,

            discount_type: '',

            discount_value: ''
        });

        const selectedProducts = computed(() => {
            return assignedProductsData.value.map(value => {
                return products.value.find(product => product.value === Number(value.product_id));
            });
        });

        const normalizeProductId = (id: unknown): number => {
            const n = Number(id);

            return Number.isNaN(n) ? 0 : n;
        };

        const labelFromAssignmentRow = (row: Record<string, unknown>) => {
            try {
                const productsRel = row.products as { description?: { name?: string } } | undefined;

                const name = productsRel?.description?.name;

                if (typeof name === 'string') {
                    const parsed = JSON.parse(name) as { en?: { name?: string } };

                    return parsed?.en?.name ?? String(row.product_id);
                }
            } catch {
                /* ignore */
            }

            return String(row.product_id ?? '');
        };

        const mergeAssignmentOptionsIntoProducts = (rows: Record<string, unknown>[]) => {
            for (const row of rows) {
                const pid = normalizeProductId(row.product_id);

                if (pid === 0) continue;

                if (products.value.some(p => p.value === pid)) continue;

                products.value.push({
                    value: pid,

                    label: labelFromAssignmentRow(row)
                });
            }
        };

        const mergeRowsIntoAccumulated = (rows: Record<string, unknown>[]) => {
            for (const row of rows) {
                const pid = normalizeProductId(row.product_id);

                if (pid !== 0) accumulatedAssignmentIds.value.add(pid);
            }

            assignedProducts.value = Array.from(accumulatedAssignmentIds.value);
        };

        const applyProductGroupProductsPayload = (pg: { data?: Record<string, unknown>[]; last_page?: number }) => {
            const rows = pg.data ?? [];

            assignedProductsData.value = rows;

            mergeRowsIntoAccumulated(rows);

            mergeAssignmentOptionsIntoProducts(rows);
        };

        /** Fetch remaining pages with Apollo.query (same query) so submit has full assignment list */

        const fetchRemainingAssignmentPages = async (fromPage: number, lastPage: number) => {
            for (let p = fromPage; p <= lastPage; p++) {
                const result = await Apollo.query({
                    query: GET_PRODUCT_GROUP_PRODUCTS,

                    variables: {
                        id: product_group_id.value,

                        page: p,

                        limit: ASSIGNMENTS_LIMIT
                    },

                    fetchPolicy: 'network-only',

                    errorPolicy: 'all'
                });

                const nextPg = result.data?.product_group_products as { data?: Record<string, unknown>[] } | undefined;

                if (nextPg?.data?.length) {
                    mergeRowsIntoAccumulated(nextPg.data);

                    mergeAssignmentOptionsIntoProducts(nextPg.data);
                }
            }
        };

        const submitHandlerForm = async () => {
            const formData = {
                product_id: assignedProducts.value,

                id: product_group_id.value
            };

            loading.value = true;

            await Apollo.mutate({
                mutation: UPDATE_GROUP_PRODUCTS,

                variables: { input: formData },

                update: (store, { data: { update_group_products } }) => {
                    modal.value.hide();

                    loading.value = false;

                    Notify.success(`${i18n.t('message.RECORD_UPDATED_SUCCESSFULLY')}`);
                }
            }).catch(() => {
                loading.value = false;
            });
        };

        const resetForm = () => {
            emitter.emit('clearInput');

            current_page.value = 1;

            editProductGroupForm.value?.resetFields();

            accumulatedAssignmentIds.value = new Set();

            pagination.value = {};
        };

        /** One-shot fetch only — watchQuery keeps subscriptions and re-emits on cache updates (infinite calls). */
        const getProducts = async () => {
            loader.value = true;
            try {
                const result = await Apollo.query({
                    query: CUP_AGREEMENT_PRODUCT,
                    errorPolicy: 'all',
                    fetchPolicy: 'network-only',
                    variables: {
                        page: 1,
                        limit: CATALOG_PRODUCTS_LIMIT
                    }
                });

                if (result.errors?.length || !result.data?.products?.data) {
                    products.value = [];
                    return;
                }

                products.value = [];
                result.data.products.data.forEach((element: Record<string, unknown>) => {
                    let label = String(element.id ?? '');
                    try {
                        const desc = element.description as { name?: string } | undefined;
                        if (desc?.name) {
                            label = JSON.parse(desc.name).en.name;
                        }
                    } catch {
                        /* keep fallback label */
                    }
                    products.value.push({
                        value: normalizeProductId(element.id),
                        label
                    });
                });
            } finally {
                loader.value = false;
            }
        };

        void getProducts();

        const getProductGroupProducts = async (page = 1, reload = false) => {
            loader.value = true;
            try {
                reload === true ? (current_page.value = 1) : false;
                clickHandler.value = getProductGroupProducts;

                const result = await Apollo.query({
                    query: GET_PRODUCT_GROUP_PRODUCTS,
                    errorPolicy: 'all',
                    fetchPolicy: 'network-only',
                    variables: {
                        page,
                        limit: ASSIGNMENTS_LIMIT,
                        id: product_group_id.value
                    }
                });

                if (result.errors?.length) {
                    return;
                }

                const pg = result.data?.product_group_products as
                    | {
                          data?: Record<string, unknown>[];
                          total?: number;
                          per_page?: number;
                          current_page?: number;
                          last_page?: number;
                      }
                    | undefined;

                if (!pg) {
                    assignedProductsData.value = [];
                    assignedProducts.value = [];
                    pagination.value = {};
                    return;
                }

                pagination.value = pg;
                applyProductGroupProductsPayload(pg);

                const lastPage = Math.max(1, Number(pg.last_page) || 1);
                const current = Math.max(1, Number(pg.current_page) || page);

                if (reload && lastPage > 1 && current === 1) {
                    await fetchRemainingAssignmentPages(2, lastPage);
                }
            } finally {
                loader.value = false;
            }
        };

        const handleAssignmentPageChange = (num: number) => {
            clickHandler.value(num);
        };

        const openAssignProductGroupModal = async (product_group: Record<string, unknown>) => {
            resetForm();

            product_group_id.value = product_group.id;

            await new Promise(resolve => setTimeout(resolve, 100));

            getProductGroupProducts(1, true);

            modal.value = new Modal(document.getElementById('modal_assign_product_group'));

            modal.value.show();
        };

        emitter.on('assignProductGroupAction', openAssignProductGroupModal);

        onUnmounted(() => {
            emitter.off('assignProductGroupAction', openAssignProductGroupModal);
        });

        return {
            loading,

            editProductGroupData,

            editProductGroupForm,

            submitHandlerForm,

            products,

            group_products,

            assignedProductsData,

            assignedProducts,

            selectedProducts,

            assignedProduct,

            pagination,

            handleAssignmentPageChange
        };
    }
});
